import { colorToCss } from "../../color/color"
import { ICrop } from "../../crops"
import { CropYear } from "../../cropYear"
import { FieldViewModel } from "../../fields"
import { groupBy, selectMany } from "../../utils/array"
import { isArray, isDate } from "../../utils/utils"
import { DomItem } from "./domItem"
import { CroppingHistoryProvider } from "./history"
import { setupPerennialCrops } from "./perennial"
import { createTimelineFields, TlCropping, TlField } from "./timeline"
import { IDateViewModel, IFieldYearViewModel, ITimelineCtrl, LayoutFunction, TimelineViewModel, ViewConfig, Years } from "./types"

interface SetupContext {

    viewConfig: ViewConfig

    startDate: Date
    endDate: Date
    dates: IDateViewModel[]

    tlfields: TlField[]

}

export function createGlobalViewModel(
    tl: ITimelineCtrl,
    fields: FieldViewModel[],
    history: CroppingHistoryProvider,
    year: CropYear,
    years: Years,
    layout: LayoutFunction): TimelineViewModel {

    const cfg = tl.viewConfig

    const tlfields = createTimelineFields(fields, history, years, layout)

    const dates = setupDates(years)

    const setup: SetupContext = {
        viewConfig: tl.viewConfig,
        ...dates,
        tlfields
    }

    const domItems = createDomItems(tl, setup, history)

    const disabledYears = setupDisabledYears(setup, domItems.fields, years)

    const vm = {

        ...setup,
        ...domItems,
        disabledYears,

        cropYear: null,

        getDurationScale: () => getDurationScale(cfg)

    }

    const cropYear = setDurationScale(vm, year)

    return {
        ...vm,
        cropYear
    }

}

function setupDates(range: Years) {

    const step = 2

    const startDate = CropYear.getYearStart(range.first)
    const endDate = CropYear.getYearEnd(range.last)

    let dates: IDateViewModel[] = []
    let date = startDate

    while (date < endDate) {

        dates.push({
            date: date,
            left: 0,
            cropyear: false
        })

        date = new Date(date.getFullYear(), date.getMonth(), 1).addMonths(step)

    }

    return {
        startDate,
        endDate,
        dates
    }

}

function setDurationScale(vm: TimelineViewModel, cropYear: CropYear) {

    vm.cropping.forEach(c => layoutCropping(vm, c))

    const cropYearStart = cropYear.StartDate
    const cropYearEnd = cropYear.EndDate.addDays(1)

    const cropYearLeft = getDatePosition(vm, cropYearStart)
    const cropYearWidth = getDatePosition(vm, cropYearEnd) - cropYearLeft

    vm.dates.forEach(d => {

        d.left = getDatePosition(vm, d.date)
        d.cropyear = (d.date >= cropYearStart) && (d.date <= cropYearEnd)

    });

    return {
        start: cropYearStart,
        end: cropYearEnd,
        left: cropYearLeft,
        width: cropYearWidth
    }

}


function getDurationScale(viewConfig: ViewConfig) {

    const minScale = 0.25

    let scale = (viewConfig.durationScale - viewConfig.durationScaleMin) / (viewConfig.durationScaleMax - viewConfig.durationScaleMin)

    scale *= 1 - minScale
    scale += minScale

    return scale

}


function setupDisabledYears(context: { viewConfig: ViewConfig, startDate: Date }, fields: DomItem[], yearRange: Years) {


    const fieldYears = fields
        .filter(f => f.vm && f.vm.isField())
        .filter(f => !!f.vm.field.Years)
        .map(f => f.vm.field.Years.filter(y => y.Disabled).map(y => ({ field: f, year: y.YearId })))

    const years = selectMany(fieldYears)
        .filter(y => (yearRange.first <= y.year) && (yearRange.last >= y.year))
        .map(y => {

            const year = new CropYear(y.year)

            const item = <IFieldYearViewModel>{

                id: `tl-disabled-${y.field.id}-${y.year}`,

                fieldId: y.field.fieldId,

                startDate: year.StartDate,
                endDate: year.EndDate,

                top: y.field.top,
                height: y.field.height,
                left: 0,
                width: 0,

                disabled: (y.year >= yearRange.last) || y.field.vm.isDisabledOnYear(y.year + 1),

                target: {
                    year: y.year,
                    field: y.field.vm,
                    fieldName: y.field.vm.fieldName
                }

            }

            layoutCropping(context, item)

            return item

        })

    return years

}

function createDomItems(tl: ITimelineCtrl, setup: SetupContext, history: CroppingHistoryProvider) {

    const cfg = setup.viewConfig

    var allitems: DomItem[] = [];

    var fields: DomItem[] = [];
    var cropping: DomItem[] = [];

    let top = 0;

    setup.tlfields.forEach(f => {

        const initTop = top;

        var fieldItems = [];

        var fielditem = new DomItem(tl, f.vm, null);

        top += cfg.fieldHeight;

        f.groups.forEach(g => {

            let items = createGroupItems(tl, setup, fielditem, g, top)

            // 170612 - If crops have been merged, they will appear multiple times
            // in the @items array, because it is built by traversing the graph
            // of crops rank by rank, and the merged crop will be referenced
            // by multiple previous ones.
            // The filter below is responsible for removing those duplicates.
            items = groupBy(items, i => i.vm.croppingId).map(g => g.values[0]);

            items = setupPerennialCrops(items, history)

            Array.prototype.push.apply(fieldItems, items);
            Array.prototype.push.apply(cropping, items);

            var bottom = Math.max.apply(null, items.map(i => i.top + i.height));

            top = bottom;

        });

        fielditem.left = 0;
        fielditem.top = initTop;
        fielditem.height = Math.max(cfg.croppingHeight + cfg.fieldHeight, top - initTop)

        top = fielditem.top + fielditem.height;

        allitems.push(fielditem);
        Array.prototype.push.apply(allitems, fieldItems);

        fields.push(fielditem);

    });

    const rightMargin = 15
    const fullHeight = top + cfg.bottomMargin
    const fullWidth = getDatePosition(setup, setup.endDate) + rightMargin

    fields.forEach(f => f.width = fullWidth)

    setupTabindex(allitems)

    return {
        allitems,
        fields,
        cropping,
        fullHeight,
        fullWidth
    }

}

export function createGroupItems(tl: ITimelineCtrl, context: SetupContext, field: DomItem, group: TlCropping[], top: number) {

    const cfg = context.viewConfig

    const items: DomItem[] = [];

    group = group.filter(c => isDate(c.vm.cropping.StartDate));

    group.forEach(c => {

        var i = new DomItem(tl, c.vm, field);

        i.rank = c.rank
        i.rowIndex = c.cropping.rowIndex
        i.rowSpan = c.cropping.rowSpan

        i.startDate = c.startDate;
        i.endDate = c.endDate;

        layoutCropping(context, i)

        i.top = top + (c.top * cfg.croppingHeight);
        i.height = (c.height * cfg.croppingHeight);

        const crop = getCrop(i.vm);

        i.uncropped = (!crop) || (!crop.CropId);

        const color = i.vm.color

        if (color && isArray(color)) {
            const alpha = i.disabled ? 0.5 : 1
            i.color = colorToCss(color, alpha)

        }

        items.push(i);

        var nextItems = createGroupItems(tl, context, field, c.nextItems, top)

        Array.prototype.push.apply(items, nextItems)

    });

    return items;
}


function setupTabindex(items: DomItem[]) {

    items = items.sort((i1, i2) => {

        var diff = i1.top - i2.top;

        if (!diff) {
            diff = i1.left - i2.left;
        }

        return diff;

    });

    items.forEach((i, index) => i.tabindex = (index + 1))

}


export function layoutCropping(context: { viewConfig: ViewConfig, startDate: Date }, i: { startDate: Date, endDate: Date, left: number, width: number }) {

    const minWidth = 70

    i.left = getDatePosition(context, i.startDate);

    if (isDate(i.endDate)) {
        var right = getDatePosition(context, i.endDate)
        i.width = Math.max(minWidth, (right - i.left) + 2)
    }
    else {
        i.width = minWidth
    }

}

export function getDatePosition(context: { viewConfig: ViewConfig, startDate: Date }, date: Date): number {

    const cfg = context.viewConfig

    var diff = Math.floor((date.valueOf() - context.startDate.valueOf()) / (1000 * 60 * 60 * 24))

    var position = Math.round(diff * cfg.durationScale);

    position += cfg.horizontalMargin;

    return position;

}

function getCrop(vm: FieldViewModel): ICrop {

    var data = vm.cropping;
    if (!data) return null;

    return data.Crop;

}
